import React, {memo, useCallback, useEffect, useRef} from 'react';

import {useSelector} from 'react-redux';
import {RootState} from '../../store';
import {CrudImpl, StorageSourceCrud} from '../../api/org/storageSource/api';
import {CrudShim} from '../../api/org/storageSource/shim';
import {toast, ToastContainer} from 'react-toastify';
import {useSearchParams} from 'react-router-dom';
import {StorageSource, StorageType} from '../../api/types/organization/StorageSource';
import {Button} from 'src/ui-components/Button/button';
import {ArrowRightSquareIcon, CopyIcon, LinkIcon, PlusIcon, SearchIcon, TrashIcon} from 'lucide-react';
import {ReactComponent as ServerIcon} from 'src/assets/icons/server-04.svg';
import {ReactComponent as PerceptPixelLogo} from 'src/assets/icons/logo.svg';
import {Input} from '../settings/profile/Profile';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from 'src/ui-components/Select';
import {DEFAULT_IMG_HOST} from '../../constants';

const shim = false;

type LoadingState = 'idle' | 'pending' | 'resolved' | 'rejected';

export const StorageIcon = memo(function StorageIcon(props: {type: StorageType; className?: string}) {
    const {type, className} = props;
    switch (type) {
        case StorageType.DEFAULT_STORAGE:
            return <PerceptPixelLogo className={className} />;
        case StorageType.REMOTE_STORAGE:
            return <ServerIcon className={className} />;
        default:
            return <LinkIcon className={className} />;
    }
});

const Form = memo(function Form(props: {
    current?: StorageSource;
    onSave: (src: StorageSource, cb: (p0: boolean) => void) => void;
    onDelete: (src: StorageSource, cb: (p0: boolean) => void) => void;
    onCancel?: () => void;
}) {
    const {current} = props;
    console.debug('Form: current', current);
    const [src, setSrc] = React.useState<StorageSource>({
        type: current?.type ?? StorageType.UNKNOWN,
        name: current?.name ?? '',
        origin: current?.origin ?? '',
        headers: current?.headers ?? [
            {
                key: '',
                value: '',
            },
        ],
        seo_enabled: current?.seo_enabled ?? false,
    });
    const [loading, setLoading] = React.useState<LoadingState>('idle');
    const userProfile = useSelector((state: RootState) => state.userProfile);

    const disabled =
        loading === 'pending' ||
        src.name.length === 0 ||
        (src.type === StorageType.REMOTE_STORAGE && src.origin.length === 0);

    return (
        <div className={'flex grow flex-col p-7'}>
            <div
                className={'flex flex-row items-center rounded-t-2xl border border-b-0 border-gray-200 bg-gray-50 p-5'}>
                <div className={'flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100'}>
                    <StorageIcon className={'h-6 w-6'} type={src.type} />
                </div>
                <span className={'pl-4 text-lg font-semibold'}>
                    {current ? 'Edit: ' + current.name : 'Create New' + (!!src.name ? `: ${src.name}` : '')}
                </span>
                <div className={'ml-auto flex flex-row gap-3'}>
                    <Button
                        variant={'secondary'}
                        onClick={() => {
                            if (!!current) {
                                props.onCancel?.();
                            } else {
                                setSrc({
                                    type: StorageType.REMOTE_STORAGE,
                                    name: '',
                                    origin: '',
                                    headers: [
                                        {
                                            key: '',
                                            value: '',
                                        },
                                    ],
                                    seo_enabled: false,
                                });
                            }
                        }}>
                        Cancel
                    </Button>
                    {!!current && (
                        <Button
                            variant={'nude'}
                            onClick={() => {
                                const confirm = window.confirm(
                                    'Are you sure you want to delete this origin? This action is irreversible.',
                                );
                                if (confirm) {
                                    const confirm2 = window.confirm(
                                        'Are you really sure? This action is irreversible.',
                                    );
                                    if (confirm2) {
                                        setLoading('pending');
                                        props.onDelete(src, (success) => {
                                            setLoading(success ? 'resolved' : 'rejected');
                                        });
                                    }
                                }
                            }}
                            data-disabled={disabled}
                            disabled={disabled}
                            className={'border border-red-700 bg-white text-red-700 data-[disabled=true]:opacity-50'}>
                            {loading === 'pending' ? 'Deleting...' : 'Delete'}
                        </Button>
                    )}
                    <Button
                        variant={'primary'}
                        data-disabled={disabled}
                        disabled={disabled}
                        className={'data-[disabled=true]:opacity-50'}
                        onClick={() => {
                            setLoading('pending');
                            // fixup headers
                            src.headers = src.headers.filter((h) => h.key.length > 0 && h.value.length > 0);
                            props.onSave(src, (success) => {
                                setLoading(success ? 'resolved' : 'rejected');
                            });
                        }}>
                        {loading === 'pending'
                            ? !!current
                                ? 'Updating...'
                                : 'Saving...'
                            : !!current
                              ? 'Update'
                              : 'Save'}
                    </Button>
                </div>
            </div>
            <div className={'grow rounded-b-2xl border border-gray-200 bg-gray-50 p-10'}>
                <div className={'flex flex-col pb-10'}>
                    <span className={'pb-1.5 text-sm font-medium text-gray-700'}>Origin Name</span>
                    <Input
                        type='text'
                        placeholder={'eg: images2023'}
                        value={src.name}
                        disabled={!!current}
                        onChange={(e) => setSrc({...src, name: e.target.value})}
                    />
                    <span className={'text-sm'}>
                        You can choose any name to identify the origin, incorporating alphabets, numbers, dash (
                        <span className={'font-mono'}>-</span>) and underscore(<span className={'font-mono'}>_</span>).
                    </span>
                    {src.name ? (
                        <div className={'flex flex-row pt-2 text-sm'}>
                            CDN URL at PerceptPixel:&nbsp;
                            <div className={'font-mono'}>
                                {DEFAULT_IMG_HOST}/
                                {userProfile.currentOrganization.alias.name ?? userProfile.currentOrganization.uid}/
                                {src.name}
                            </div>
                            <div
                                className={'ml-1 h-4 w-4 cursor-pointer'}
                                onClick={() => {
                                    navigator.clipboard.writeText(
                                        `${DEFAULT_IMG_HOST}/${userProfile.currentOrganization.alias.name ?? userProfile.currentOrganization.uid}/${src.name}`,
                                    );
                                    toast.success('Copied to clipboard', {
                                        position: 'top-right',
                                        autoClose: 3000,
                                    });
                                }}>
                                <CopyIcon />
                            </div>
                        </div>
                    ) : null}
                </div>
                <div className={'flex flex-col pb-10'}>
                    <span className={'pb-1.5 text-sm font-medium text-gray-700'}>Origin Type</span>
                    <Select
                        disabled={!!current}
                        value={src.type === StorageType.UNKNOWN ? undefined : src.type.toString()}
                        onValueChange={(value: string) => {
                            setSrc({...src, type: parseInt(value)});
                        }}>
                        <SelectTrigger className='w-[180px]'>
                            <SelectValue placeholder='Select a Storage Type' />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                <SelectLabel>Storage Type</SelectLabel>
                                <SelectItem value={StorageType.DEFAULT_STORAGE.toString()}>
                                    <div className={'flex flex-row items-center'}>
                                        <PerceptPixelLogo className={'h-4 w-4'} />
                                        <span className={'pl-2'}>PerceptPixel Storage</span>
                                    </div>
                                </SelectItem>
                                <SelectItem value={StorageType.REMOTE_STORAGE.toString()}>
                                    <div className={'flex flex-row items-center'}>
                                        <ServerIcon className={'h-4 w-4'} />
                                        <span className={'pl-2'}>Remote Storage</span>
                                    </div>
                                </SelectItem>
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>

                {src.type === StorageType.REMOTE_STORAGE && (
                    <div className={'flex flex-col pb-10'}>
                        <span className={'pb-1.5 text-sm font-medium text-gray-700'}>URL Endpoint</span>
                        <Input
                            type='text'
                            placeholder={'eg: https://files.acmecorp.com/images2023'}
                            value={src.origin}
                            onChange={(e) => setSrc({...src, origin: e.target.value})}
                        />
                        <span className={'text-sm'}>URL endpoint link.</span>
                    </div>
                )}
                {src.type === StorageType.REMOTE_STORAGE && (
                    <div className={'flex flex-col pb-10'}>
                        <span className={'pb-1.5 text-sm font-medium text-gray-700'}>Headers</span>
                        <div className={'flex flex-col gap-6'}>
                            {src.headers.map((h, i) => {
                                return (
                                    <div key={i} className={'flex flex-row items-center gap-2'}>
                                        <Input
                                            type='text'
                                            className={'bg-blue-50'}
                                            placeholder={'Enter Header name'}
                                            value={h.key}
                                            onChange={(e) => {
                                                const headers = src.headers.slice();
                                                headers[i] = {...h, key: e.target.value};
                                                setSrc({...src, headers});
                                            }}
                                        />
                                        <div>
                                            <ArrowRightSquareIcon className={'h-6 w-6 text-gray-600'} />
                                        </div>
                                        <Input
                                            type='text'
                                            placeholder={'Enter Header value'}
                                            className={'bg-blue-50'}
                                            value={h.value}
                                            onChange={(e) => {
                                                const headers = src.headers.slice();
                                                headers[i] = {...h, value: e.target.value};
                                                setSrc({...src, headers});
                                            }}
                                        />
                                        <div
                                            onClick={() => {
                                                const confirm = window.confirm(
                                                    'Are you sure you want to delete this header?',
                                                );
                                                if (confirm) {
                                                    const headers = src.headers.slice();
                                                    headers.splice(i, 1);
                                                    setSrc({...src, headers});
                                                }
                                            }}>
                                            <TrashIcon className={'h-6 w-6 cursor-pointer text-gray-600'} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <Button
                            className={'mt-6'}
                            size={'sm'}
                            variant={'outline'}
                            icon
                            onClick={() => {
                                setSrc({...src, headers: [...src.headers, {key: '', value: ''}]});
                            }}>
                            <PlusIcon />
                            <span>Add Header</span>
                        </Button>
                    </div>
                )}
                <div className={'flex flex-row items-center gap-2'}>
                    <input
                        type='checkbox'
                        checked={src.seo_enabled}
                        onChange={(e) => setSrc({...src, seo_enabled: e.target.checked})}
                    />
                    <span>SEO Enabled</span>
                </div>
            </div>
        </div>
    );
});

const SearchBtn = memo(function SearchBtn(props: {search: string; onSearch: (search: string) => void}) {
    return (
        <Button icon variant={'nude'} className={'w-full'}>
            <SearchIcon />
            <Input
                type={'search'}
                value={props.search}
                placeholder={'Search...'}
                className={'w-full'}
                onChange={(e) => props.onSearch(e.target.value)}
            />
        </Button>
    );
});

export default function RemoteStorageScreen() {
    const [list, setList] = React.useState<StorageSource[]>([]);
    const [loading, setLoading] = React.useState<LoadingState>('idle');
    const [error, setError] = React.useState<Error | undefined>(undefined);
    const userProfile = useSelector((state: RootState) => state.userProfile);
    const api = useRef<StorageSourceCrud | undefined>(undefined);

    const [params, setParams] = useSearchParams();
    const [current, setCurrent] = React.useState<StorageSource | undefined>(undefined);

    const [search, setSearch] = React.useState('');

    useEffect(() => {
        if (api.current === undefined || api.current?.orgId !== userProfile.currentOrganization.uid) {
            if (shim) {
                api.current = new CrudShim(userProfile.currentOrganization.uid);
            } else {
                api.current = new CrudImpl(userProfile.currentOrganization.uid);
            }
        }
        if (loading === 'idle') {
            setLoading('pending');
            api.current
                ?.list()
                .then((res) => {
                    setList(res);
                    setLoading('resolved');
                })
                .catch((err) => {
                    setError(err);
                    setLoading('rejected');
                });
        }
        if (loading === 'resolved') {
            const name = params.get('name');
            console.debug('RemoteStorageScreen: name', name);
            if (name) {
                const found = list.find((s) => s.name === name);
                console.debug('RemoteStorageScreen: found', found);
                if (found) {
                    setCurrent(found);
                }
            }
        }
    }, [loading, list, userProfile.currentOrganization.uid, params]);

    const onSelect = useCallback(
        (src?: StorageSource) => {
            setParams({name: src?.name ?? ''});
            setCurrent(src);

            // reset search output
            if (!!src) {
                setSearch('');
            }
        },
        [setParams, setCurrent, setSearch],
    );

    const onSave = useCallback(
        (src: StorageSource, cb: (p0: boolean) => void) => {
            const upsert = !!current;
            (upsert ? api.current?.update(src) : api.current?.create(src))!
                .then((res) => {
                    if (upsert) {
                        setList(list.map((s) => (s.name === res.name ? res : s)));
                    } else {
                        setList([...list, res]);
                    }
                    onSelect(res);
                    toast.success(`Storage source "${src.name}" ${upsert ? 'updated' : 'created'} successfully.`, {
                        position: 'top-right',
                        autoClose: 5000,
                    });
                    cb(true);
                })
                .catch((err) => {
                    toast.error(`Failed to save storage source "${src.name}": ` + (err.message ?? 'Unknown error'), {
                        position: 'top-right',
                        autoClose: 5000,
                    });
                    cb(false);
                });
        },
        [api, list, current, onSelect],
    );

    const onDelete = useCallback(
        (src: StorageSource, cb: (p0: boolean) => void) => {
            if (current === undefined) return;

            api.current
                ?.delete(src)
                .then(() => {
                    setList(list.filter((s) => s.name !== src.name));
                    onSelect(undefined);
                    toast.success(`Storage source "${src.name}" deleted successfully.`, {
                        position: 'top-right',
                        autoClose: 5000,
                    });
                    cb(true);
                })
                .catch((err) => {
                    toast.error(`Failed to delete storage source "${src.name}": ` + (err.message ?? 'Unknown error'), {
                        position: 'top-right',
                        autoClose: 5000,
                    });
                    cb(false);
                });
        },
        [api, list, current, onSelect],
    );

    const onCancel = useCallback(() => {
        onSelect(undefined);
    }, [onSelect]);

    const CreateBtn = useCallback(
        function CreateBtn() {
            return (
                <Button
                    icon
                    className={'w-full'}
                    onClick={() => {
                        onSelect(undefined);
                    }}>
                    <PlusIcon className={'mr-auto'} />
                    <span className={'mr-auto'}>Create New</span>
                </Button>
            );
        },
        [onSelect],
    );

    const FilterList = useCallback(
        function FilterList() {
            const filtered = list.filter(
                (s) =>
                    s.name.toLowerCase().includes(search.toLowerCase()) ||
                    s.origin.toLowerCase().includes(search.toLowerCase()),
            );

            return (
                <div className={'flex flex-col gap-2'}>
                    {filtered.map((src) => (
                        <Button
                            icon
                            data-active={current?.name === src.name ? 'true' : 'false'}
                            className={'w-full data-[active=true]:bg-gray-100'}
                            variant={'nude'}
                            onClick={() => {
                                onSelect(src);
                            }}>
                            <div className={'flex grow flex-row items-center'}>
                                <div className={'h-6 w-6'}>
                                    <StorageIcon type={src.type} className={'h-6 w-6'} />
                                </div>
                                <div className={'flex grow pl-2'}>{src.name}</div>
                            </div>
                        </Button>
                    ))}
                    {search.length > 0 && filtered.length === 0 ? (
                        <div className={'flex flex-row items-center justify-center p-4 text-gray-500'}>No results</div>
                    ) : null}
                    {loading === 'resolved' && search.length == 0 && filtered.length == 0 ? (
                        <div className={'flex flex-row items-center justify-center p-4 text-gray-500'}>
                            No storage sources found
                        </div>
                    ) : null}
                </div>
            );
        },
        [list, search, onSelect, current],
    );

    return error ? (
        <>
            <div className={'flex flex-row items-center justify-center p-4 text-red-500'}>
                {error.message ?? 'Unknown error'}
            </div>
        </>
    ) : (
        <>
            <ToastContainer />
            <div className={'flex h-screen w-full'}>
                <div className={'flex w-60 flex-col gap-4 border border-r-gray-200 p-4'}>
                    <SearchBtn search={search} onSearch={setSearch} />
                    <CreateBtn />
                    <FilterList />
                    {loading === 'pending' ? (
                        <div className={'flex flex-row items-center justify-center p-4'}>
                            <span>Loading...</span>
                        </div>
                    ) : null}
                </div>
                <Form key={current?.name} current={current} onSave={onSave} onDelete={onDelete} onCancel={onCancel} />
            </div>
        </>
    );
}
